import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../component/header";
import Footer from "../component/Footer";
import Banner from "../component/Banner";
import Breadcrumb from "../component/Breadcrumb";
import BannerImg from "../image/gallaryBanner.png";
import { Search, Images, Layers3, Album } from "lucide-react";

const QUEST_FEATURED_GALLERY_ID = "6a1f2a3cd388b57911134962";

function imageUrl(path) {
  if (!path) return "";
  return path.startsWith("http") ? path : path;
}

const GalleryCardSkeleton = () => (
  <div
    className="relative overflow-hidden border border-black/5 bg-white shadow-sm"
    style={{ height: "500px", borderRadius: "12px" }}
  >
    <div className="h-full w-full animate-pulse bg-gradient-to-br from-[#e8eeee] via-[#f7fafa] to-[#dce5e5]" />
    <div className="absolute bottom-8 right-4 w-[76%] space-y-3">
      <div className="ml-auto h-8 w-2/3 animate-pulse rounded bg-white/80" />
      <div className="ml-auto h-3 w-full animate-pulse rounded bg-white/65" />
      <div className="ml-auto h-3 w-4/5 animate-pulse rounded bg-white/55" />
      <div className="ml-auto mt-5 h-3 w-1/2 animate-pulse rounded bg-[#9eeff4]/80" />
    </div>
  </div>
);

const workflowSteps = [
  { label: "1", title: "Choose a category", description: "Start with Quest, Spotlight, Sculptural, or Showcase.", Icon: Images },
  { label: "2", title: "Open a collection", description: "Each category page lists the available galleries.", Icon: Layers3 },
  { label: "3", title: "Enter an album", description: "Album pages hold the published images and commentary.", Icon: Album },
];

function Gallary() {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoadingCategories(true);
    fetch("/api/gallery/categories")
      .then((r) => r.json())
      .then((result) => {
        if (!mounted || !result.success || !result.data?.length) return;
        setCategories(
          result.data.map((c) => ({
            key: c.key,
            title: c.title,
            description: c.description,
            image: imageUrl(c.image || c.featuredGalleryCoverImage),
            galleryCount: c.galleryCount || 0,
            featuredGalleryId: c.featuredGalleryId || null,
          }))
        );
      })
      .catch(() => {})
      .finally(() => {
        if (mounted) setLoadingCategories(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories.filter(
      (c) => c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
    );
  }, [categories, search]);

  return (
    <div>
      <Header />
      <Banner
        backgroundImage={BannerImg}
        heading="Gallery"
        paragraph="Browse collection categories, open a collection, then step into the album that holds the published images."
        minHeight="100vh"
        padding="80px 0"
        bton="Members please sign-in"
      />
      <Breadcrumb items={[{ label: "Gallery" }]} />

      <section className="main-lwbl px-4 pt-10 pb-2">
        <div className="container m-auto">
          <div className="grid gap-4 md:grid-cols-3">
            {workflowSteps.map((step) => (
              <div key={step.label} className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
                  <step.Icon size={20} />
                </div>
                <h3 className="font-cinzel text-xl font-bold text-gray-900">{step.title}</h3>
                <p className="mt-2 font-outfit text-sm text-gray-500">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="main-lwbl px-4 pt-10 pb-4">
        <div className="container m-auto">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#008aa0]">
                Collection Categories
              </p>
              <h2 className="mt-2 font-cinzel text-3xl text-[#101819]">Start with a category</h2>
            </div>
            <p className="font-outfit text-sm text-gray-400">
              {loadingCategories ? "Loading categories..." : `${filteredCategories.length} categor${filteredCategories.length === 1 ? "y" : "ies"}`}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative max-w-sm w-full">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search categories..."
                className="w-full rounded-full border border-gray-200 bg-white py-2.5 pl-10 pr-4 font-outfit text-sm outline-none focus:border-cyan-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Grid */}
      <section className="main-gallery mt-5 rounded-[30px] bg-[#F5F5DB] px-[1rem] pb-[80px]">
        <div className="container m-auto">
          {filteredCategories.length === 0 ? (
            loadingCategories ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
                {Array.from({ length: 4 }).map((_, index) => (
                  <GalleryCardSkeleton key={index} />
                ))}
              </div>
            ) : (
            <p className="py-10 text-center font-outfit text-sm text-gray-500">No categories match your search.</p>
            )
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {filteredCategories.map(({ key, title, description, image, galleryCount, featuredGalleryId }) => (
                <Link
                  key={key}
                  to={featuredGalleryId
                      ? `/gallery/${key}/${featuredGalleryId}`
                      : key === "quests"
                        ? `/gallery/quests/${QUEST_FEATURED_GALLERY_ID}`
                      : `/gallery/${key}`}
                  className="group block"
                >
                  <div
                    className="relative overflow-hidden border border-black/5 bg-white shadow-sm transition duration-300 group-hover:-translate-y-1 group-hover:shadow-xl"
                    style={{ height: "500px", borderRadius: "12px" }}
                  >
                    {image ? (
                      <img
                        src={image}
                        className="h-full w-full object-cover transition duration-500 scale-105 group-hover:scale-110"
                        alt={title}
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-[#dce5e5] via-[#f7fafa] to-[#c9d7da]" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/78 via-black/18 to-transparent" />
                    <div className="absolute bottom-8 right-4 max-w-[84%] text-right">
                      <h3 className="font-cinzel text-[30px] leading-tight text-white">{title}</h3>
                      <p className="mt-2 font-outfit text-sm leading-5 text-white">{description}</p>
                      <p className="mt-4 font-outfit text-xs font-semibold uppercase tracking-[0.16em] text-[#00F0FB]">
                        {galleryCount ? `${galleryCount} collections` : "View collections"}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Gallary;
